/**
 * Planning → Nog in te plannen (Joris, 9 sep 2026).
 *
 * Eén scherm met wat nog geen plek in de planning heeft:
 *  A. projecten met draaidagen waarvan montage/animatie/feedback niet (volledig)
 *     is ingepland — met de offerte-uren ernaast en de ruimte tot de deadline.
 *     Uitklappen toont de echte planning van het project (dezelfde takenrijen
 *     als Planner To-Do) plus wie wat heeft;
 *  B. projecten met taken zonder eigenaar, mét hun planning erbij;
 *  C. de deadline-check: welke deliverables hebben een signaal — ter plekke aan
 *     te passen, met ongedaan maken.
 *
 * Het planbord (Joris, 25 sep): de "te plannen"-bak die vroeger bovenin het
 * Rooster stond, boven hetzelfde RoosterGrid, zodat je hier een taak op een
 * persoon en dag sleept. Klap je een project open, dan staat het bord direct
 * onder die projectrij en toont de bak alleen dat project (eerst stond het onder
 * alle ~23 projecten: ver scrollen). Zonder gekozen project is het één regel
 * onderaan, uit te klappen voor alle ongeplande taken. Rust: één signaal per
 * project (de rest in de tooltip), hele uren, uitleg achter de (?).
 *
 * Alle rekenregels zitten in de engine (/Api/inplannen/overzicht); dit scherm
 * toont alleen. Besluiten 9 sep: 4 productieve uur per werkdag, ingepland =
 * eigenaar én datum, Strippenkaart buiten beschouwing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Collapse, FormControl, IconButton, InputLabel, MenuItem,
  Link, Popover, Select, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon, HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { leesCache, lopendeLading, useTraagVerversen } from '../../utils/planningCache';
import { haalInplannen, sleutels, taakGewijzigd } from '../../utils/planningBronnen';
import { useKlantAfkortingen } from '../../utils/klantAfkortingen';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import {
  datumKort, disciplinesVoorTab, heeftOpenWerk, nogGeenTaak, nogGeenTaakTekst, projectSignalen, uurHeel,
} from '../../utils/inplannen';
import ProjectPlanning from './ProjectPlanning';
import Planbord from './Planbord';
import OverzichtPerProject from '../PlanningOverzicht/OverzichtPerProject';

const FONT = '"Inter", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const LICHT = '#9ca3af';
const LIJN = '#eef0f3';
const ROOD = '#b91c1c';
// Nog eens vragen na een verlopen engine-stand (zie laad).
export const NAVRAAG_MS = 4000;
const NAVRAAG_MAX = 3;

const DISCIPLINES = [
  { key: 'montage', label: 'Montage' },
  { key: 'animatie', label: 'Animatie' },
  { key: 'feedback', label: 'Feedback' },
];

const TOELICHTING = {
  A: 'Lopende projecten (geen Strippenkaart) met minstens één draaidag waarvan de postproductie niet volledig is ingepland. Gepland = eigenaar én datum; zonder datum telt half. Klap een project open voor de planning en wie wat heeft; direct daaronder staat het planbord met alleen de taken van dat project.',
  B: 'Projecten met montage-, animatie- of feedbacktaken zonder eigenaar, met hun planning erbij. Klap een project open en sleep de taken uit de bak direct eronder op een persoon en dag.',
  C: 'Projecten met een deliverable-signaal, in het planningsgrid: klap een project open voor de deliverables en taken, pas de deadline aan in de kop van de deliverable, kopieer en plak zoals in een spreadsheet.',
};

const projectLabel = (rij, kortKlant) => {
  const klant = kortKlant ? kortKlant(rij.klant) : rij.klant;
  return `${rij.nummer} · ${klant || ''}${klant && rij.naam ? ' — ' : ''}${rij.naam || ''}`;
};

function Balk({ gepland, offerte }) {
  const pct = offerte > 0 ? Math.min(100, Math.round((gepland / offerte) * 100)) : 0;
  return (
    <Box sx={{ mt: 0.5, height: 3, borderRadius: 2, bgcolor: LIJN, position: 'relative', overflow: 'hidden', maxWidth: 140 }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: '#9ca3af' }} />
    </Box>
  );
}

/** Eén signaal per project: het belangrijkste; rood alleen bij een echt probleem. De rest in de tooltip. */
function SignaalTekst({ rij }) {
  const { hoofd, overige } = projectSignalen(rij);
  if (!hoofd) return null;
  const kleur = hoofd.ernst === 'probleem' ? ROOD : hoofd.ernst === 'let_op' ? TEKST : GEDEMPT;
  const titel = (
    <Box sx={{ p: 0.25 }}>
      {[hoofd, ...overige].map((sg) => (
        <Box key={sg.code} sx={{ mb: 0.5 }}>
          <b>{sg.tekst}</b>{sg.uitleg ? ` — ${sg.uitleg}` : ''}
        </Box>
      ))}
    </Box>
  );
  return (
    <Tooltip title={titel} arrow placement="left">
      <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12.5, color: kleur, fontWeight: hoofd.ernst === 'probleem' ? 600 : 400, cursor: 'help', whiteSpace: 'nowrap' }}>
        {hoofd.tekst}
        {overige.length > 0 && <Box component="span" sx={{ color: LICHT, fontWeight: 400 }}> +{overige.length}</Box>}
      </Typography>
    </Tooltip>
  );
}

// Beide tabs gaan over montage/animatie: de projectlink landt op de fase Postproductie
// (taken van die fase + "Postproductie inplannen"), niet op de fase waar het project
// toevallig staat (wiki 27 sep). weergave=flow: ook wie de tabbladen gewend is, ziet de fase.
export const postproductieLink = (id) => `/projects/${encodeURIComponent(id)}?weergave=flow&fase=postproductie`;

function ProjectLink({ rij, kortKlant }) {
  return (
    <Box>
      <Typography component={RouterLink} to={postproductieLink(rij.id)} onClick={(e) => e.stopPropagation()}
        sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: TEKST, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
        {projectLabel(rij, kortKlant)}
      </Typography>
      {rij.pl && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: GEDEMPT }}>PL {rij.pl}</Typography>}
    </Box>
  );
}

/** Wat zit er achter "43 / 96 u"? Voor de tooltip op de discipline-cel. */
function DisciplineTooltip({ rij, discipline, children }) {
  const d = rij.disciplines?.[discipline] || {};
  const per = { gepland: [], toegewezen: [], zonder_eigenaar: [] };
  (rij.taken || []).filter((t) => t.discipline === discipline).forEach((t) => per[t.toestand]?.push(t));
  const regel = (t) => `${t.name} · ${uurHeel(Number(t.hours) || 0)} u${t.assignees?.length ? ` · ${t.assignees.join(', ')}` : ''}${t.startDate ? ` · ${datumKort(String(t.startDate).slice(0, 10))}` : ''}`;
  const blok = (kop, lijst) => (lijst.length ? (
    <Box key={kop} sx={{ mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, opacity: 0.8 }}>{kop} ({lijst.length})</Typography>
      {lijst.slice(0, 8).map((t) => <Typography key={t.id} sx={{ fontFamily: FONT, fontSize: 11.5 }}>{regel(t)}</Typography>)}
      {lijst.length > 8 && <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.7 }}>+{lijst.length - 8} meer</Typography>}
    </Box>
  ) : null);
  const metEigenaar = (d.aantal || 0) - (d.aantalZonderEigenaar || 0);
  const kern = [
    metEigenaar > 0 && `${metEigenaar} met eigenaar`,
    d.verstreken > 0 && `${uurHeel(d.verstreken)} u al voorbij`,
    d.aantalZonderEigenaar > 0 && `${d.aantalZonderEigenaar} zonder eigenaar`,
    d.toegewezen > 0 && `${uurHeel(d.toegewezen)} u zonder datum`,
  ].filter(Boolean).join(' · ');
  const inhoud = [blok('Gepland', per.gepland), blok('Toegewezen, zonder datum', per.toegewezen), blok('Zonder eigenaar', per.zonder_eigenaar)].filter(Boolean);
  return (
    <Tooltip arrow placement="top" title={(
      <Box sx={{ p: 0.5 }}>
        {kern && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, mb: inhoud.length ? 0.75 : 0 }}>{kern}</Typography>}
        {inhoud.length ? inhoud : 'Nog geen taken voor deze discipline'}
      </Box>
    )}>
      <Box sx={{ cursor: 'help' }}>{children}</Box>
    </Tooltip>
  );
}

const tabelSx = {
  width: '100%', borderCollapse: 'collapse', minWidth: 860, fontFamily: FONT, fontSize: 13,
  '& th': { textAlign: 'left', fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: GEDEMPT, px: 1.5, py: 0.9, borderBottom: `1px solid ${LIJN}`, whiteSpace: 'nowrap' },
  '& td': { px: 1.5, py: 1.1, borderBottom: `1px solid ${LIJN}`, verticalAlign: 'top' },
};

function BlokA({ rijen, disciplines, kortKlant, onGewijzigd, gekozen, onKies, bord }) {
  const [open, setOpen] = useState({});
  if (!rijen.length) {
    return <Typography sx={{ fontFamily: FONT, fontSize: 13, color: GEDEMPT, py: 2 }}>Alles wat gedraaid is of gaat draaien, staat ingepland.</Typography>;
  }
  const kolommen = DISCIPLINES.filter((d) => disciplines.includes(d.key));
  const aantalKolommen = 5 + kolommen.length;
  return (
    <Box sx={{ overflowX: 'auto', border: `1px solid ${LIJN}`, borderRadius: 2, bgcolor: '#fff' }}>
      <Box component="table" sx={tabelSx}>
        <thead>
          <tr>
            <th style={{ width: 28 }} aria-label="uitklappen" />
            <th>Project</th>
            <th>Draaidagen</th>
            {kolommen.map((k) => <th key={k.key}>{k.label}</th>)}
            <th>Eerste deadline</th>
            <th>Signaal</th>
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => {
            const dd = rij.draaidagen || {};
            const delen = [];
            if (dd.gedraaid) delen.push(`${dd.gedraaid} gedraaid`);
            if (dd.gepland) delen.push(`${dd.gepland} gepland`);
            if (dd.opties) delen.push(`${dd.opties} optie${dd.opties > 1 ? 's' : ''}`);
            const isOpen = !!open[rij.id];
            const toggle = () => {
              setOpen((p) => ({ ...p, [rij.id]: !p[rij.id] }));
              if (!isOpen) onKies(rij); else if (gekozen === rij.id) onKies(null);
            };
            const eersteDl = (rij.deliverables || []).find((x) => x.deadline === rij.eersteDeadline);
            const actief = gekozen === rij.id;
            return (
              <React.Fragment key={rij.id}>
                <tr onClick={toggle} aria-expanded={isOpen}
                  style={{ cursor: 'pointer', background: actief ? '#f6f7f9' : undefined, boxShadow: actief ? 'inset 2px 0 0 #526075' : undefined }}>
                  <td style={{ paddingRight: 0 }}>{isOpen ? <ExpandMoreIcon sx={{ fontSize: 18, color: GEDEMPT }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: LICHT }} />}</td>
                  <td><ProjectLink rij={rij} kortKlant={kortKlant} /></td>
                  <td>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEKST }}>{delen.join(' · ') || '–'}</Typography>
                    {dd.laatste && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: GEDEMPT }}>laatste {datumKort(dd.laatste)}</Typography>}
                  </td>
                  {kolommen.map((k) => {
                    const d = rij.disciplines?.[k.key] || {};
                    const geen = !d.offerte && !d.aantal;
                    return (
                      <td key={k.key} style={{ minWidth: 110 }}>
                        {geen ? <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: LICHT }}>–</Typography> : (
                          <DisciplineTooltip rij={rij} discipline={k.key}>
                            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEKST, fontVariantNumeric: 'tabular-nums' }}>
                              {uurHeel(d.geteld)}<Box component="span" sx={{ color: GEDEMPT }}> / {d.offerte ? `${uurHeel(d.offerte)} u` : '? u'}</Box>
                            </Typography>
                            <Balk gepland={d.geteld} offerte={d.offerte} />
                          </DisciplineTooltip>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {rij.eersteDeadline ? (
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: TEKST }}>
                        {datumKort(rij.eersteDeadline)}
                        {eersteDl?.type && <Box component="span" sx={{ color: GEDEMPT }}> · {String(eersteDl.type).toLowerCase()}</Box>}
                      </Typography>
                    ) : <Typography sx={{ fontFamily: FONT, fontSize: 13, color: LICHT }}>–</Typography>}
                  </td>
                  <td><SignaalTekst rij={rij} /></td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={aantalKolommen} style={{ paddingTop: 6, paddingBottom: 16, background: '#fcfcfd' }}>
                      <ProjectPlanning rij={rij} disciplines={disciplines} onGewijzigd={onGewijzigd} />
                      {actief && bord(rij)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

function BlokB({ projecten, disciplines, kortKlant, onGewijzigd, gekozen, onKies, bord }) {
  const [open, setOpen] = useState({});
  const lijst = projecten.filter((p) => (p.taken || []).some((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline)));
  if (!lijst.length) return <Typography sx={{ fontFamily: FONT, fontSize: 13, color: GEDEMPT, py: 2 }}>Geen taken zonder eigenaar.</Typography>;
  return (
    <Box sx={{ border: `1px solid ${LIJN}`, borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
      {lijst.map((p, i) => {
        const zonder = (p.taken || []).filter((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline));
        const urenZonder = zonder.reduce((s, t) => s + (Number(t.hours) || 0), 0);
        const isOpen = !!open[p.id];
        const urgent = String(p.prio || '').startsWith('Prio 1');
        const actief = gekozen === p.id;
        const toggle = () => {
          setOpen((o) => ({ ...o, [p.id]: !o[p.id] }));
          if (!isOpen) onKies(p); else if (actief) onKies(null);
        };
        return (
          <Box key={p.id} sx={{ borderTop: i ? `1px solid ${LIJN}` : 0 }}>
            <Box onClick={toggle} aria-expanded={isOpen}
              sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', bgcolor: actief ? '#f6f7f9' : 'transparent', boxShadow: actief ? 'inset 2px 0 0 #526075' : 'none' }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: 18, color: GEDEMPT }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: LICHT }} />}
              <Box sx={{ flex: 1 }}><ProjectLink rij={p} kortKlant={kortKlant} /></Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: urgent ? TEKST : GEDEMPT, fontWeight: urgent ? 600 : 400 }}>{p.prio}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEKST, minWidth: 190, textAlign: 'right' }}>{zonder.length} zonder eigenaar · {uurHeel(urenZonder)} u</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, minWidth: 110, textAlign: 'right' }}>{p.eersteDeadline ? `deadline ${datumKort(p.eersteDeadline)}` : ''}</Typography>
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, bgcolor: '#fcfcfd' }}>
                <ProjectPlanning rij={p} disciplines={disciplines} onGewijzigd={onGewijzigd} />
                {actief && bord(p)}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * De deadline-check is het planningsgrid van Overzicht per project, ingebed
 * (Joris, 9 sep: kopiëren/plakken, deliverables in- en uitklappen, en op
 * projectniveau meteen zien wat er mist). Per project één signaal in de kop
 * (de rest in de tooltip); per deliverable de signalen naast de deadline, die
 * daar direct aan te passen is.
 */
function BlokC({ rijen, onGewijzigd }) {
  const perProject = useMemo(() => Object.fromEntries(rijen.map((r) => [r.id, r])), [rijen]);
  const deliverableSignalen = useMemo(() => {
    const uit = {};
    rijen.forEach((r) => (r.deliverables || []).forEach((d) => { if (d.signalen?.length) uit[d.id] = d.signalen; }));
    return uit;
  }, [rijen]);
  const kopExtra = useCallback((p) => {
    const rij = perProject[p.id];
    if (!rij) return null;
    return <Box component="span" onClick={(e) => e.stopPropagation()}><SignaalTekst rij={rij} /></Box>;
  }, [perProject]);
  if (!rijen.length) return <Typography sx={{ fontFamily: FONT, fontSize: 13, color: GEDEMPT, py: 2 }}>Geen deadlines met een signaal.</Typography>;
  return <OverzichtPerProject ingebed projectIds={rijen.map((r) => r.id)} kopExtra={kopExtra} deliverableSignalen={deliverableSignalen} onGewijzigd={onGewijzigd} />;
}

/**
 * Lege bak van het gekozen project: "geen taken" is niet "alles gepland" zolang er
 * offerte-uren zijn die nog geen taak zijn (Joris, 25 sep: 7584 had 96 u montage).
 * Taken maak je in het project (Deliverables → taken genereren).
 */
function LegeBak({ rij, disciplines }) {
  const lijst = nogGeenTaak(rij, disciplines);
  if (!lijst.length) return 'Geen taken van dit project in de bak — alles heeft een persoon en dag.';
  return (
    <>
      {nogGeenTaakTekst(lijst)}
      {' · '}
      <Link component={RouterLink} to={`/projects/${rij.id}?paneel=deliverables`} underline="hover"
        sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: '#526075' }}>
        taken aanmaken
      </Link>
    </>
  );
}

/** Zonder gekozen project: het planbord als één regel onderaan, uit te klappen voor alle ongeplande taken. */
function BordIngeklapt({ onOpen }) {
  return (
    <Box component="section" aria-label="Inplannen"
      sx={{ mt: 2, px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', border: `1px solid ${LIJN}`, borderRadius: 2, bgcolor: '#fff' }}>
      <ChevronRightIcon sx={{ fontSize: 18, color: LICHT }} />
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: TEKST }}>Inplannen</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, flex: 1 }}>Kies een project om in te plannen</Typography>
      <Button size="small" onClick={onOpen} sx={{ textTransform: 'none', fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: '#526075' }}>
        Alle ongeplande taken
      </Button>
    </Box>
  );
}

/** (?) met de uitleg van het tabblad, de norm en de productkoppeling — achter een klik (rust). */
function Uitleg({ blok, data }) {
  const [anker, setAnker] = useState(null);
  const pm = data?.productmap;
  return (
    <>
      <Tooltip title="Uitleg"><IconButton size="small" aria-label="Uitleg" onClick={(e) => setAnker(e.currentTarget)}><HelpIcon fontSize="small" sx={{ color: GEDEMPT }} /></IconButton></Tooltip>
      <Popover open={!!anker} anchorEl={anker} onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.75, maxWidth: 420 } } }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEKST, lineHeight: 1.5 }}>{TOELICHTING[blok]}</Typography>
        {blok === 'B' && data?.blokB?.totaal && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: TEKST, mt: 1 }}>
            Totaal zonder eigenaar: montage {uurHeel(data.blokB.totaal.montage)} u · animatie {uurHeel(data.blokB.totaal.animatie)} u · feedback {uurHeel(data.blokB.totaal.feedback)} u.
          </Typography>
        )}
        {pm && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: GEDEMPT, mt: 1.25 }}>
            Norm {pm.normUrenPerDag} u per werkdag · producten: {['montage', 'animatie', 'feedback'].map((d) => (pm[d] || []).join('/')).join(', ')}
            {' · '}<Typography component={RouterLink} to="/admin/settings" sx={{ fontFamily: FONT, fontSize: 12, color: '#526075' }}>instellen</Typography>
          </Typography>
        )}
      </Popover>
    </>
  );
}

export default function Inplannen() {
  // Beginstand uit de gedeelde planningcache: terug naar deze tab = meteen de vorige lijst.
  const [data, setData] = useState(() => leesCache(sleutels.inplannen())?.waarde || null);
  const [laden, setLaden] = useState(() => !leesCache(sleutels.inplannen()));
  const { bezig: verversend, volg: volgVerversen } = useTraagVerversen();
  const [fout, setFout] = useState(null);
  const [pl, setPl] = useState('');
  const [tab, setTab] = useState('alle');
  const [blok, setBlok] = useState('A');
  const kortKlant = useKlantAfkortingen();
  // Gekozen project (laatst opengeklapt): de bak van het planbord toont alleen dat project.
  const [gekozen, setGekozen] = useState(null); // { id, label }
  const kies = useCallback((rij) => setGekozen(rij ? { id: rij.id, label: projectLabel(rij, kortKlant) } : null), [kortKlant]);
  // Capaciteit/Rooster → "werk inplannen" linkt hierheen met ?persoon=&datum=.
  const [searchParams] = useSearchParams();
  const linkPersoon = searchParams.get('persoon');
  const linkDatum = searchParams.get('datum');

  // `vers` (verversknop, na een wijziging) = engine laat herberekenen en de cache wordt
  // omzeild; bij binnenkomen antwoordt de cache en ververst hij stil op de achtergrond.
  const vraagRef = useRef(0);
  // De engine geeft een verlopen stand meteen terug (`verouderd`) en rekent op de
  // achtergrond opnieuw; dan vragen we een paar seconden later nog eens, zonder hem
  // opnieuw te laten rekenen. Maximaal drie keer.
  const naVraagTimer = useRef(null);
  const naVragen = useRef(0);
  const laad = useCallback(async (vers = false, { navraag = false } = {}) => {
    const vraag = ++vraagRef.current;
    const actueel = () => vraagRef.current === vraag;
    clearTimeout(naVraagTimer.current);
    if (!navraag) naVragen.current = 0;
    const toon = (nieuw) => {
      if (!actueel()) return;
      setData(nieuw);
      if (nieuw?.verouderd && naVragen.current < NAVRAAG_MAX) {
        naVragen.current += 1;
        clearTimeout(naVraagTimer.current);
        naVraagTimer.current = setTimeout(() => laad(false, { navraag: true }), NAVRAAG_MS);
      }
    };
    if (!navraag) { setLaden(true); setFout(null); }
    try {
      const d = await haalInplannen({ forceer: vers || navraag, herberekenen: vers, bijVerversing: toon });
      if (!actueel()) return;
      toon(d);
      volgVerversen(lopendeLading(sleutels.inplannen()));
    } catch (e) {
      if (actueel() && !navraag) setFout(e.message || 'Laden mislukt');
    } finally {
      if (actueel()) setLaden(false);
    }
  }, [volgVerversen]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => () => clearTimeout(naVraagTimer.current), []);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Na een deadline-wijziging: de regel meteen bijwerken, en de signalen even
  // later vers uit de engine halen (die rekent 'past niet' en 'ruimte' opnieuw).
  const hertelTimer = useRef(null);
  // Na afvinken of een deadline-wijziging: even later vers uit de engine halen,
  // die telt uren en signalen opnieuw. Wie snel meerdere taken afvinkt, krijgt één verversing.
  const hertelLater = useCallback(() => {
    // De andere planningstabs tonen deze taak nu verouderd: hun stand vervalt meteen.
    taakGewijzigd({ behalve: [sleutels.inplannen()] });
    clearTimeout(hertelTimer.current);
    hertelTimer.current = setTimeout(() => laad(true), 2500);
  }, [laad]);

  const disciplines = disciplinesVoorTab(tab);
  const filterPl = useCallback((rijen) => (pl ? rijen.filter((r) => r.pl === pl) : rijen), [pl]);

  const blokA = useMemo(() => filterPl(data?.blokA || []).filter((r) => heeftOpenWerk(r, disciplines)), [data, filterPl, disciplines]);
  const blokC = useMemo(() => filterPl(data?.blokC || []), [data, filterPl]);
  const blokB = useMemo(() => filterPl(data?.blokB?.projecten || []), [data, filterPl]);
  const aantalB = blokB.reduce((s, p) => s + (p.taken || []).filter((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline)).length, 0);

  // Een toewijzing op het planbord: de lijst even later laten hertellen (de rooster- en
  // capaciteitsstand heeft het planbord zelf al ongeldig gemaakt).
  const planbordGewijzigd = useCallback(() => {
    clearTimeout(hertelTimer.current);
    hertelTimer.current = setTimeout(() => laad(true), 2500);
  }, [laad]);
  const kiesBlok = (v) => { setBlok(v); setGekozen(null); };

  // Het bord onder de uitgeklapte projectrij (alleen het gekozen project).
  const bord = useCallback((rij) => (
    <Planbord
      key={rij.id}
      inline
      project={gekozen}
      tab={tab}
      linkPersoon={linkPersoon}
      linkDatum={linkDatum}
      onGewijzigd={planbordGewijzigd}
      leegProject={<LegeBak rij={rij} disciplines={disciplines} />}
    />
  ), [gekozen, tab, linkPersoon, linkDatum, planbordGewijzigd, disciplines]);
  // Staat het gekozen project (nog) in de lijst? Anders (ander filter) het bord onderaan.
  const gekozenZichtbaar = !!gekozen && (blok === 'A'
    ? blokA.some((r) => r.id === gekozen.id)
    : blokB.some((p) => p.id === gekozen.id && (p.taken || []).some((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline))));
  // Onderaan: ingeklapt tot één regel, tenzij je via een link (persoon + dag) binnenkomt.
  const [bordOpen, setBordOpen] = useState(() => !!linkPersoon);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 1.5, md: 2 }, pb: 6 }}>
      {/* "Nog in te plannen" staat als tab in de kopbalk (PlanningTabs); de uitleg zit achter de (?) */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Tabs value={blok} onChange={(_, v) => kiesBlok(v)} sx={{ minHeight: 36, '& .MuiTab-root': { textTransform: 'none', fontFamily: FONT, fontWeight: 600, fontSize: 13, minHeight: 36, px: 1.25 } }}>
          <Tab value="A" label={`Gedraaid, montage nog niet (${blokA.length})`} />
          <Tab value="B" label={`Zonder eigenaar (${aantalB})`} />
          <Tab value="C" label={`Deadline-check (${blokC.length})`} />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {verversend && (
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={10} thickness={6} sx={{ color: LICHT }} />
            <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, color: LICHT }}>bijwerken…</Typography>
          </Box>
        )}
        <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontFamily: FONT, fontSize: 12.5, px: 1.25, py: 0.3 } }}>
          <ToggleButton value="alle">Alles</ToggleButton>
          <ToggleButton value="editors">Editors</ToggleButton>
          <ToggleButton value="animators">Animators</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Projectleider</InputLabel>
          <Select label="Projectleider" value={pl} onChange={(e) => setPl(e.target.value)} sx={{ fontFamily: FONT, fontSize: 13.5, bgcolor: '#fff' }}>
            <MenuItem value=""><em>Alle PL's</em></MenuItem>
            {(data?.pls || []).map((naam) => <MenuItem key={naam} value={naam} sx={{ fontSize: 13.5 }}>{naam}</MenuItem>)}
          </Select>
        </FormControl>
        <Uitleg blok={blok} data={data} />
        <Tooltip title="Opnieuw berekenen"><span><IconButton size="small" aria-label="Opnieuw berekenen" onClick={() => laad(true)} disabled={laden}><RefreshIcon fontSize="small" sx={{ color: GEDEMPT }} /></IconButton></span></Tooltip>
      </Stack>

      {fout && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => laad(true)}>Opnieuw</Button>}>{fout}</Alert>}
      {laden && !data ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box> : (
        <>
          {blok === 'A' && <BlokA rijen={blokA} disciplines={disciplines} kortKlant={kortKlant} onGewijzigd={hertelLater} gekozen={gekozen?.id} onKies={kies} bord={bord} />}
          {blok === 'B' && <BlokB projecten={blokB} disciplines={disciplines} kortKlant={kortKlant} onGewijzigd={hertelLater} gekozen={gekozen?.id} onKies={kies} bord={bord} />}
          {blok === 'C' && <BlokC rijen={blokC} onGewijzigd={hertelLater} />}
        </>
      )}

      {blok !== 'C' && !gekozenZichtbaar && (bordOpen ? (
        <Box sx={{ mt: 2 }}>
          <Planbord
            project={null}
            tab={tab}
            linkPersoon={linkPersoon}
            linkDatum={linkDatum}
            onGewijzigd={planbordGewijzigd}
            onInklappen={() => setBordOpen(false)}
          />
        </Box>
      ) : <BordIngeklapt onOpen={() => setBordOpen(true)} />)}
    </Box>
  );
}
